<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ImagePlus, Layers, Palette, Save, SlidersHorizontal, Upload } from '@lucide/vue'
import AppShell from '@/components/AppShell.vue'
import FormSelect from '@/components/FormSelect.vue'
import ColorField from '@/components/ColorField.vue'
import RangeField from '@/components/RangeField.vue'
import ToggleField from '@/components/ToggleField.vue'
import PopoverMenu from '@/components/PopoverMenu.vue'
import MetricBar from '@/components/MetricBar.vue'
import { useAppearanceStore } from '@/stores/appearance'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/services/api'
import { appearanceDefaults } from '@/utils/appearance'
import { prepareBackground } from '@/utils/background-upload'
import type { AppearanceSettings } from '@/types/monitoring'

const { t } = useI18n()
const appearance = useAppearanceStore()
const auth = useAuthStore()
const draft = ref<AppearanceSettings>({ ...appearance.settings })
const tab = ref<'background' | 'cards' | 'general'>('background')
const ready = ref(false)
const loading = ref(false)
const uploading = ref(false)
const uploadInput = ref<HTMLInputElement | null>(null)
const error = ref('')
const saved = ref(false)
const dirty = computed(() => JSON.stringify(draft.value) !== JSON.stringify(appearance.settings))
const modeOptions = computed(() =>
  (['solid', 'gradient', 'image'] as const).map((value) => ({
    value,
    label: t(`design.${value}`),
  })),
)
const themeOptions = computed(() =>
  (['light', 'dark', 'system'] as const).map((value) => ({
    value,
    label: t(`appearance.${value}`),
  })),
)
const filterOptions = computed(() =>
  (['none', 'soft', 'monochrome', 'warm', 'cool'] as const).map((value) => ({
    value,
    label: t(`design.${value}`),
  })),
)
const motionOptions = computed(() =>
  (['none', 'drift', 'breathe'] as const).map((value) => ({
    value,
    label: t(`design.${value === 'none' ? 'still' : value}`),
  })),
)
watch(
  draft,
  (value) => {
    if (ready.value) {
      appearance.preview(value)
      saved.value = false
    }
  },
  { deep: true },
)

function discard(): void {
  draft.value = { ...appearance.settings }
  appearance.preview(null)
  saved.value = false
  error.value = ''
}
function reset(): void {
  const group =
    tab.value === 'general'
      ? ['theme', 'locale', 'faviconUrl']
      : tab.value === 'cards'
        ? [
            'cardGlass',
            'cardOpacity',
            'cardGradientStart',
            'cardGradientEnd',
            'cardGradientAngle',
            'cardBlur',
          ]
        : [
            'backgroundMode',
            'backgroundColor',
            'backgroundUrl',
            'gradientStart',
            'gradientEnd',
            'gradientAngle',
            'gradientAnimated',
            'animationSeconds',
            'imageFilter',
            'imageBrightness',
            'imageBlur',
            'imageOverlayColor',
            'imageOverlayOpacity',
            'imageMotion',
          ]
  for (const key of group as (keyof AppearanceSettings)[])
    Object.assign(draft.value, { [key]: appearanceDefaults[key] })
}
async function save(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    await appearance.save(auth.token, draft.value)
    draft.value = { ...appearance.settings }
    await Promise.resolve()
    saved.value = true
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : t('errors.saveAppearance')
  } finally {
    loading.value = false
  }
}
async function upload(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  uploading.value = true
  error.value = ''
  try {
    const image = await prepareBackground(file)
    draft.value.backgroundUrl = await api.uploadBackground(auth.token, image)
    draft.value.backgroundMode = 'image'
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : ''
    error.value = t(
      message === 'invalid-image'
        ? 'design.invalidImage'
        : message === 'image-too-large'
          ? 'design.imageTooLarge'
          : 'design.uploadFailed',
    )
  } finally {
    uploading.value = false
    input.value = ''
  }
}
onMounted(async () => {
  try {
    await appearance.initialize(auth.token, auth.authenticated)
    draft.value = { ...appearance.settings }
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : t('errors.loadSettings')
  } finally {
    ready.value = true
  }
})
onBeforeUnmount(() => appearance.preview(null))
</script>

<template>
  <AppShell>
    <div class="mx-auto max-w-5xl">
      <div class="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 class="page-title">{{ $t('nav.appearance') }}</h1>
        <div
          class="inline-flex rounded-xl bg-slate-100/70 p-1 dark:bg-slate-800"
          role="tablist"
          :aria-label="$t('nav.appearance')"
        >
          <button
            v-for="item in ['background', 'cards', 'general'] as const"
            :id="`appearance-tab-${item}`"
            :key="item"
            type="button"
            role="tab"
            :aria-selected="tab === item"
            :aria-controls="`appearance-panel-${item}`"
            class="tab-button !px-3 sm:!px-4"
            @click="tab = item"
          >
            {{ $t(`design.${item}`) }}
          </button>
        </div>
      </div>
      <form @submit.prevent="save">
        <div class="grid items-start gap-6 lg:grid-cols-[1.05fr_1fr]">
          <section
            :id="`appearance-panel-${tab}`"
            class="panel p-5 sm:p-6"
            role="tabpanel"
            :aria-labelledby="`appearance-tab-${tab}`"
          >
            <fieldset :disabled="!ready || loading || uploading" class="space-y-6">
              <template v-if="tab === 'background'">
                <div>
                  <label class="field-label">{{ $t('design.background') }}</label
                  ><FormSelect
                    v-model="draft.backgroundMode"
                    :options="modeOptions"
                    :label="$t('design.background')"
                  />
                </div>
                <ColorField
                  v-if="draft.backgroundMode === 'solid'"
                  v-model="draft.backgroundColor"
                  :label="$t('design.color')"
                />
                <template v-else-if="draft.backgroundMode === 'gradient'">
                  <div class="grid grid-cols-2 gap-4">
                    <ColorField
                      v-model="draft.gradientStart"
                      :label="$t('design.startColor')"
                    /><ColorField v-model="draft.gradientEnd" :label="$t('design.endColor')" />
                  </div>
                  <RangeField
                    v-model="draft.gradientAngle"
                    :label="$t('design.angle')"
                    :min="0"
                    :max="360"
                    unit="°"
                  />
                  <ToggleField v-model="draft.gradientAnimated" :label="$t('design.flow')" />
                  <RangeField
                    v-if="draft.gradientAnimated"
                    v-model="draft.animationSeconds"
                    :label="$t('design.duration')"
                    :min="8"
                    :max="120"
                    unit="s"
                  />
                </template>
                <template v-else>
                  <div
                    class="rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-5 text-center dark:border-slate-600"
                  >
                    <ImagePlus class="mx-auto mb-3 size-7 text-slate-400" aria-hidden="true" />
                    <input
                      ref="uploadInput"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      class="hidden"
                      :aria-label="$t('design.upload')"
                      @change="upload"
                    />
                    <button
                      type="button"
                      class="btn-quiet gap-2"
                      :disabled="uploading"
                      @click="uploadInput?.click()"
                    >
                      <Upload class="size-4" aria-hidden="true" />{{
                        $t(uploading ? 'design.uploading' : 'design.upload')
                      }}
                    </button>
                    <p class="mt-3 text-xs text-slate-400">{{ $t('design.imageHint') }}</p>
                    <p v-if="draft.backgroundUrl" class="mt-2 text-xs text-moss-700">
                      {{ $t('design.image') }} ✓
                    </p>
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="field-label">{{ $t('design.filters') }}</label
                      ><FormSelect
                        v-model="draft.imageFilter"
                        :label="$t('design.filters')"
                        :options="filterOptions"
                      />
                    </div>
                    <div>
                      <label class="field-label">{{ $t('design.motion') }}</label
                      ><FormSelect
                        v-model="draft.imageMotion"
                        :label="$t('design.motion')"
                        :options="motionOptions"
                      />
                    </div>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <PopoverMenu :label="$t('design.display')" role="dialog" :width="310">
                      <template #trigger
                        ><SlidersHorizontal class="size-4" aria-hidden="true" />{{
                          $t('design.display')
                        }}</template
                      >
                      <div class="space-y-5 p-3">
                        <RangeField
                          v-model="draft.imageBrightness"
                          :label="$t('design.brightness')"
                          :min="25"
                          :max="150"
                          unit="%"
                        />
                        <RangeField
                          v-model="draft.imageBlur"
                          :label="$t('design.blur')"
                          :min="0"
                          :max="20"
                          unit="px"
                        />
                        <ColorField
                          v-model="draft.imageOverlayColor"
                          :label="$t('design.overlay')"
                        />
                        <RangeField
                          v-model="draft.imageOverlayOpacity"
                          :label="$t('design.overlayOpacity')"
                          :min="0"
                          :max="80"
                          unit="%"
                        />
                        <RangeField
                          v-if="draft.imageMotion !== 'none'"
                          v-model="draft.animationSeconds"
                          :label="$t('design.duration')"
                          :min="8"
                          :max="120"
                          unit="s"
                        />
                      </div>
                    </PopoverMenu>
                    <PopoverMenu :label="$t('design.advanced')" role="dialog" :width="310"
                      ><div class="p-3">
                        <label for="background-link" class="field-label">{{
                          $t('design.imageUrl')
                        }}</label>
                        <input
                          id="background-link"
                          v-model="draft.backgroundUrl"
                          type="text"
                          class="field"
                          placeholder="https://…"
                          maxlength="2048"
                        /></div
                    ></PopoverMenu>
                  </div>
                </template>
              </template>
              <template v-else-if="tab === 'cards'">
                <ToggleField v-model="draft.cardGlass" :label="$t('design.glass')" />
                <template v-if="draft.cardGlass">
                  <RangeField
                    v-model="draft.cardOpacity"
                    :label="$t('design.opacity')"
                    :min="20"
                    :max="100"
                    unit="%"
                  />
                  <RangeField
                    v-model="draft.cardBlur"
                    :label="$t('design.blur')"
                    :min="0"
                    :max="40"
                    unit="px"
                  />
                  <div class="grid grid-cols-2 gap-4">
                    <ColorField
                      v-model="draft.cardGradientStart"
                      :label="$t('design.startColor')"
                    /><ColorField v-model="draft.cardGradientEnd" :label="$t('design.endColor')" />
                  </div>
                  <RangeField
                    v-model="draft.cardGradientAngle"
                    :label="$t('design.angle')"
                    :min="0"
                    :max="360"
                    unit="°"
                  />
                </template>
                <Layers
                  v-else
                  class="mx-auto size-16 text-slate-200 dark:text-slate-600"
                  aria-hidden="true"
                />
              </template>
              <template v-else>
                <div>
                  <label class="field-label">{{ $t('appearance.theme') }}</label
                  ><FormSelect
                    v-model="draft.theme"
                    :options="themeOptions"
                    :label="$t('appearance.theme')"
                  />
                </div>
                <div>
                  <label class="field-label">{{ $t('appearance.language') }}</label
                  ><FormSelect
                    v-model="draft.locale"
                    :label="$t('appearance.language')"
                    :options="[
                      { value: 'en', label: 'English' },
                      { value: 'zh-CN', label: '简体中文' },
                    ]"
                  />
                </div>
                <div>
                  <label for="favicon-link" class="field-label">{{
                    $t('appearance.favicon')
                  }}</label
                  ><input
                    id="favicon-link"
                    v-model="draft.faviconUrl"
                    type="text"
                    class="field"
                    placeholder="https://…"
                    maxlength="2048"
                  />
                </div>
              </template>
            </fieldset>
          </section>

          <aside class="lg:sticky lg:top-24">
            <div
              class="relative grid min-h-[350px] place-items-center overflow-hidden rounded-2xl border border-slate-200/70 p-6 sm:p-9"
            >
              <div class="w-full max-w-sm">
                <div class="mb-4 flex items-center gap-2 text-xs font-medium text-slate-500">
                  <Palette class="size-4" aria-hidden="true" />{{ $t('design.preview') }}
                </div>
                <article class="panel p-6">
                  <div class="mb-6 flex items-center justify-between gap-3">
                    <div>
                      <h2 class="font-display text-xl font-bold text-ink">Pulsewatch</h2>
                      <p class="mt-1 text-xs text-slate-400">#01</p>
                    </div>
                    <span class="inline-flex items-center gap-1.5 text-xs text-moss-700"
                      ><span class="size-1.5 rounded-full bg-moss-500" />{{
                        $t('status.online')
                      }}</span
                    >
                  </div>
                  <div class="space-y-5">
                    <MetricBar :label="$t('card.cpu')" :value="24" /><MetricBar
                      :label="$t('card.memory')"
                      :value="58"
                    />
                    <div
                      class="flex items-center justify-between border-t border-slate-200/50 pt-4 text-xs"
                    >
                      <span class="text-slate-500">{{ $t('card.latency') }}</span
                      ><span class="font-mono font-medium text-ink">32 ms</span>
                    </div>
                  </div>
                </article>
              </div>
            </div>
            <p class="mt-3 text-center text-xs text-slate-500">{{ $t('design.previewHint') }}</p>
          </aside>
        </div>
        <p v-if="error" role="alert" class="mt-5 text-sm text-red-600">{{ error }}</p>
        <div class="mt-6 flex flex-wrap items-center justify-between gap-4">
          <PopoverMenu :label="$t('design.more')" :width="190"
            ><template #default="{ close }">
              <button
                type="button"
                class="menu-item"
                role="menuitem"
                :disabled="loading || uploading"
                @click="
                  () => {
                    reset()
                    close()
                  }
                "
              >
                {{ $t('design.reset') }}
              </button>
              <button
                type="button"
                class="menu-item"
                role="menuitem"
                :disabled="loading || uploading || !dirty"
                @click="
                  () => {
                    discard()
                    close()
                  }
                "
              >
                {{ $t('design.discard') }}
              </button>
            </template></PopoverMenu
          >
          <div class="flex items-center gap-3">
            <p v-if="saved" role="status" class="text-sm text-moss-700">{{ $t('design.saved') }}</p>
            <button
              type="submit"
              class="btn-primary gap-2"
              :disabled="!ready || loading || uploading || !dirty"
            >
              <Save class="size-4" aria-hidden="true" />{{
                $t(loading ? 'common.saving' : 'design.save')
              }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </AppShell>
</template>
